/* eslint-disable no-param-reassign */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import {
    Modal,
    Form,
    Col,
    Select,
    Row,
    Button,
    Divider,
    Spin,
    ModalProps,
    Typography,
} from 'antd';
import { SelectValue } from 'antd/lib/select';
import moment from 'moment';
import { saveAs } from 'file-saver';
import {
    CustomFooterContainer,
    ButtonGoBack,
    ButtonExport,
    GlobalStyleModalLogsTotem,
} from './styles';
import { useGetLogsErrorsTotem } from '../../../store/Totem/useGetLogsErrorsTotem';
import { DATE_TO_PICK_TOTEM_LOGS } from '../../../helpers/constants';
import {
    getPropertyNamesFromDatePickerExtends,
    EDatePickerExtends,
    EnumKeyTypeDatePickerExtends,
} from './types';

interface Props extends ModalProps {
    totemId?: string;
}

export const ExportLogsTotemModal = ({ totemId, onCancel }: Props) => {
    const [startDate, setStartDate] = useState(
        moment().format('YYYY-MM-DDT00:00:00'),
    );
    const [endDate, setEndDate] = useState(
        moment().format('YYYY-MM-DDT23:59:59'),
    );
    const [form] = Form.useForm();
    const [valuesFormSubmit, setValuesFormSubmit] = useState(
        form.getFieldsValue(true),
    );
    const listRef = useRef(null);
    const {
        data: logs,
        isFetching: isFetchingLogs,
        isInitialLoading: isLoadingLogsList,
        fetchNextPage: fetchNextPageLogsList,
        hasNextPage: hasNextPageLogsList,
    } = useGetLogsErrorsTotem({
        startDate: valuesFormSubmit.initialDate,
        endDate: valuesFormSubmit.finalDate,
        totemId,
    });

    const flattenedPaginatedLogs = useMemo(
        () => logs?.pages.flatMap((page) => page.data) ?? [],
        [logs],
    );

    const onSubmit = () => {
        const values = form.getFieldsValue(true);
        setValuesFormSubmit(values);
    };

    const exportLogs = () => {
        const logsFormatted = logs?.pages.map((el) => {
            el.data.forEach((obj) => {
                obj.timestamp = obj.timestamp
                    ? moment(obj.timestamp).format('YYYY-MM-DDTHH:mm:ss')
                    : 'Não informado';
                obj.receivedAt = obj.receivedAt
                    ? moment(obj.receivedAt).format('YYYY-MM-DDTHH:mm:ss')
                    : 'Não informado';
                obj.payload.date = obj.payload.date
                    ? moment(obj.payload?.date).format('YYYY-MM-DDTHH:mm:ss')
                    : 'Não informado';
            });

            return el.data;
        });
        const jsonString = JSON.stringify(logsFormatted, null, 2);
        const blob = new Blob([jsonString], {
            type: 'text/plain;charset=utf-8',
        });
        saveAs(
            blob,
            `logs-totem-${moment(startDate).format(
                'DD-MM-YYYY_HH-mm-ss',
            )} - ${moment(endDate).format('DD-MM-YYYY_HH-mm-ss')}.txt`,
        );
    };

    const handleScroll = () => {
        if (listRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = listRef.current;
            if (
                scrollTop + clientHeight >= scrollHeight &&
                hasNextPageLogsList &&
                !isLoadingLogsList
            ) {
                fetchNextPageLogsList();
            }
        }
    };

    const getDatePickerOptions = () => {
        return getPropertyNamesFromDatePickerExtends()?.map((pick) => ({
            label: EDatePickerExtends[pick as EnumKeyTypeDatePickerExtends],
            value: pick,
        }));
    };

    const onChangeDate = (value: SelectValue) => {
        const { initial, final } =
            DATE_TO_PICK_TOTEM_LOGS[value as EnumKeyTypeDatePickerExtends];
        setStartDate(moment(initial).format('YYYY-MM-DDT00:00:00'));
        setEndDate(moment(final).format('YYYY-MM-DDT23:59:59'));

        form.setFieldsValue({
            initialDate: moment(initial).format('YYYY-MM-DDT00:00:00'),
            finalDate: moment(final).format('YYYY-MM-DDT23:59:59'),
        });
    };

    const onChangeTimeInterval = (
        values: any,
        formatString: [string, string],
    ) => {
        form.setFieldsValue({
            initialDate: moment(startDate).format(
                `YYYY-MM-DDT${formatString[0]}`,
            ),
            finalDate: moment(endDate).format(`YYYY-MM-DDT${formatString[1]}`),
        });
    };

    useEffect(() => {
        onChangeDate('today');
        const values = form.getFieldsValue(true);
        setValuesFormSubmit(values);
    }, []);

    const { Paragraph } = Typography;

    return (
        <>
            <GlobalStyleModalLogsTotem />
            <Modal
                styles={{
                    body: { height: 520, padding: '20px 50px' },
                }}
                style={{ minWidth: '15vw' }}
                onCancel={onCancel}
                title={
                    <h3
                        style={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            marginBottom: 0,
                        }}
                    >
                        Logs de Erros
                    </h3>
                }
                open
                footer={
                    <CustomFooterContainer>
                        <ButtonGoBack
                            onClick={onCancel}
                            style={{ width: '200px' }}
                        />
                        <ButtonExport
                            onClick={exportLogs}
                            disabled={flattenedPaginatedLogs.length === 0}
                            className="green-btn"
                            style={{
                                width: '200px',
                            }}
                        />
                    </CustomFooterContainer>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onSubmit}
                    initialValues={{
                        date: 'today',
                    }}
                    style={{ padding: 24 }}
                >
                    <Col xs={24} sm={23} md={17} lg={15} xl={15} xxl={13}>
                        <Form.Item label="Período" name="date">
                            <Select
                                options={getDatePickerOptions()}
                                onChange={onChangeDate}
                                showSearch={false}
                                allowClear={false}
                            />
                        </Form.Item>
                    </Col>
                    <Row style={{ paddingTop: '2rem' }}>
                        <Col xs={8} sm={8} md={8} lg={7} xl={7} xxl={6}>
                            <Button
                                htmlType="submit"
                                className="default-btn"
                                icon={<SearchOutlined />}
                            >
                                Pesquisar
                            </Button>
                        </Col>
                        <Col
                            offset={1}
                            xs={8}
                            sm={8}
                            md={8}
                            lg={7}
                            xl={7}
                            xxl={6}
                        >
                            <Button
                                danger
                                htmlType="reset"
                                style={{ width: '100%' }}
                                onClick={() => {
                                    onChangeDate('today');
                                    const values = form.getFieldsValue(true);
                                    setValuesFormSubmit(values);
                                }}
                            >
                                Limpar
                            </Button>
                        </Col>
                    </Row>
                </Form>
                <Divider style={{ borderWidth: 2 }} />
                <ul
                    style={{
                        padding: '0px',
                        paddingLeft: '40px',
                        overflowY: 'scroll',
                        height: '30vh',
                    }}
                    ref={listRef}
                    onScroll={handleScroll}
                >
                    {flattenedPaginatedLogs.map((el, index) => (
                        <li key={index}>
                            {moment(el.timestamp).format('YYYY-MM-DDTHH:mm:ss')}{' '}
                            - {el.payload.title.toLowerCase()} -{' '}
                            {el.payload.message.toLowerCase() ??
                                el.payload.error.toLowerCase()}
                        </li>
                    ))}
                    {!flattenedPaginatedLogs.length && !isFetchingLogs && (
                        <Paragraph>Nenhum log foi encontrado</Paragraph>
                    )}
                    {isFetchingLogs && (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '10px',
                            }}
                        >
                            <Spin />
                        </div>
                    )}
                </ul>
            </Modal>
        </>
    );
};
